import React, { Component, Fragment } from 'react'
import classnames from 'classnames'
import debounce from 'lodash.debounce'
import { delay, pickByIndex } from 'utils'
import { isRuleSetValid } from 'utils/segment'
import { append, isEmpty, without, uniq } from 'ramda'
import AccountSettingsModal from './AccountSettingsModal'
import { Div, Input, Loader, Span, NavigationHeader, IconButton } from '@driftt/tide-core'
import { access, tracking } from '@driftt/traffic-control'

import { remindAccountNotifications } from 'modules/notifications/follow'

import { getABMPlaybookTotal } from 'api/metrics'
import { getAccounts } from 'api/accounts'
import { setAccountsFollow } from 'api/settings'
import { getAccountsForSegment, getAccountsByRule } from 'api/segments'

import { AccountsTableColumns } from 'components/AccountsTable/constants'

import AccountsOnboarding from 'components/AccountsOnboarding'
import AccountsTable from 'components/AccountsTable'
import AccountTableToolbar from 'components/AccountTableToolbar'
import AccountsTableZeroState from 'components/AccountsTableZeroState'
import CreateOrEditAccountSidebar from 'components/CreateOrEditAccountSidebar'
import ManageAccounts from 'components/ManageAccounts'
import DeleteAccounts from 'components/DeleteAccounts'
import Paginator from 'components/Paginator'
import queryString from 'query-string';

import './style.css'

const foreverDismissOnboarding = window.localStorage.getItem('dismiss-account-onboarding') || false

const Loading = ({ hasFinished }) => (
  <Div className={classnames("accounts-table-loader", {'accounts-table-loader--loaded': hasFinished})}>
    <Span>Getting your accounts</Span>
    <Loader className="accounts-table-loader-animation" />
  </Div>
)

class AccountsHome extends Component {

  constructor(props) {
    super(props)

    this.state = {
      deleteAccount: null,
      accountsLoading: true,
      accountsHasLoaded: false,
      newAccountModalOpen: false,
      confirmDeleteModalOpen: false,
      resultSwap: false,
      showOnboarding: !JSON.parse(foreverDismissOnboarding),
      hasShownFollowReminder: false,
      isFetchingNew: false,
      hasABMPlaybooks: false,
      deleteLoading: false,
      filterMode: false,
      size: 25,
      index: 0,
      searchString: '',
      selectedAccount: null,
      filter: {},
      showCreateOrEditAccountSidebar: false,
      sort: { dir: 'asc', property: 'name' },
      selectedRows: [],
      segment: null,
      hasLoadedSegmentById: null,
      isDeletingAll: false,
      canDeleteAll: false,
      modalIsOpen: false
    }
  }

  componentDidMount() {
    this.fetchAccounts()
    this.getPlaybookCounts()
  }

  shouldRemindAboutNotifications = () => {
    const { settings } = this.props
    const { showOnboarding, hasShownFollowReminder } = this.state
    const hasFollowAccounts = !!settings.follow_accounts
    const shouldShow = !hasFollowAccounts && !showOnboarding && !hasShownFollowReminder
    if (shouldShow) {
      remindAccountNotifications(this.updateAccountFollow)
      this.setState({
        hasShownFollowReminder: true
      })
    }
  }

  onChangeSize = ({ value }) => {
    this.setState({
      size: value,
      index: 0,
      isFetchingNew: true
    }, this.fetchAccounts)
  }

  toggleCreateOrEditNewAccountSidebar = (account = {}) => {
    this.setState(prevState => ({
      selectedAccount: !isEmpty(account) ? account : null,
      showCreateOrEditAccountSidebar: !prevState.showCreateOrEditAccountSidebar
    }))
  }

  toggleConfirmDeleteModal = (isDeletingAll: false) => () => {
    this.setState(prevState => ({
      confirmDeleteModalOpen: !prevState.confirmDeleteModalOpen,
      isDeletingAll: isDeletingAll
    }))
  }

  setDeleteLoading = () => {
    this.setState({
      deleteLoading: true
    }, this.toggleConfirmDeleteModal())
  }

  setSingleDelete = (account, index) => {
    this.setState(prevState => ({
      selectedRows: (account && account.accountId) ? [index] : prevState.selectedRows
    }), this.toggleConfirmDeleteModal())
  }

  setSearchString = event => {
    event.persist()

    this.setState({
      searchString: event.target.value
    })

    const doSearch = () => {
      if (event.target.value) {
        tracking.trackEvent('Manage Accounts - Accounts Searched', {
          query: event.target.value
        })
      }

      this.setState({
        filter: { nameordomain: event.target.value},
        isFetchingNew: true
      }, this.fetchAccounts)
    }

    this.debouncedSearch && this.debouncedSearch.cancel()
    this.debouncedSearch = debounce(doSearch, 700)
    this.debouncedSearch()

    !event.target.value && this.debouncedSearch.flush()
  }

  setAccountsLoaded = async () => {

    const start = () =>
      this.setState({
        accountsHasLoaded: true,
        isFetchingNew: false,
      })

    const complete = () =>
      this.setState({
        accountsLoading: false,
        selectedRows: [],
        deleteLoading: false,
        filterMode: true,
        resultSwap: false
      }, this.shouldRemindAboutNotifications)

    delay(start, 200)
    delay(complete, 200)
  }

  navigateForward = () => {
    this.setState(prevState => ({
      index: prevState.index + 1,
      isFetchingNew: true
    }), this.fetchAccounts)
  }

  navigateBackward = () => {
    this.setState(prevState => ({
      index: prevState.index - 1,
      isFetchingNew: true
    }), this.fetchAccounts)
  }

  toggleSelected = (toggled, id) => {
    const toggle = toggled ? append : without

    this.setState(prevState => ({
      selected: toggle(id, prevState.selected)
    }))
  }

  dismissOnboarding = () => {
    this.setState({
      showOnboarding: false
    })
  }

  dismissOnboardingForever = () => {
    window.localStorage.setItem('dismiss-account-onboarding', true)
    this.dismissOnboarding()
  }

  getPlaybookCounts = async () => {
    try {
      const { data } = await getABMPlaybookTotal()
      this.setState({
        hasABMPlaybooks: !!data.length
      })

    } catch (err) {

    }
  }

  toggleAllSelected = checked => {
    const { accounts } = this.props
    const selectAll = accounts.results.map((x, index) => index)
    const newSelected = checked ? selectAll : []
    this.setState({
      selectedRows: newSelected,
      canDeleteAll: checked
    })
  }

  getBulkSelect = () => {
    const { selectedRows } = this.state
    const { accounts } = this.props
    const isChecked = accounts.results && accounts.results.length && selectedRows.length === accounts.results.length

    return (
      <Input
        type="checkbox"
        onChange={this.toggleAllSelected}
        checked={isChecked}
      />
    )
  }

  selectRow = index => {
    this.setState(prevState => ({
      selectedRows: uniq([...prevState.selectedRows, index]).sort()
    }))
  }

  removeRow = index => {
    this.setState(prevState => ({
      selectedRows: without([index], prevState.selectedRows)
    }))
  }

  getFormattedColumns = () => {
    const { sort, segment } = this.state


    return AccountsTableColumns(this.updateSort, this.getBulkSelect)
      .map(column => {
        if (column.value === sort.property) {
          column.sorting = true
          column.direction = sort.dir
        } else {
          column.sorting = false
        }

        if (column.onClick && column.sortable) {
         column.sortable = !!segment ? false : true
        }

        return column
      })
  }

  fetchAccountsForSegment = async () => {
    const { onReceiveAccounts, showTransientNotification, match } = this.props
    const { size, index, filter, sort } = this.state
    const { segmentId } = match.params

    try {
      const { data:accountsResponse } = await getAccountsForSegment(segmentId, { sort, size, filter, index: index * size })
      onReceiveAccounts(accountsResponse)
      this.setAccountsLoaded()
      this.setState({
        segment: { id: segmentId, ...accountsResponse },
        hasLoadedSegmentById: true
      })
    } catch (err) {
      showTransientNotification({ type: 'error', message: 'We had a problem loading your accounts.'})
      this.setAccountsLoaded()
    }
  }

  fetchAccountsWithRuleSet =  async () => {
    const { onReceiveAccounts, showTransientNotification } = this.props
    const { size, index, filter, sort, segment } = this.state
    const { ruleSet } = segment

    try {
      const { data:accountsResponse } = await getAccountsByRule(ruleSet, { sort, size, filter, index: index * size })
      onReceiveAccounts(accountsResponse)
      this.setAccountsLoaded()

    } catch (err) {
      showTransientNotification({ type: 'error', message: 'We had a problem loading your accounts.'})
      this.setAccountsLoaded()
    }
  }

  fetchAccounts = async () => {
    const { onReceiveAccounts, showTransientNotification, match } = this.props
    const { size, index, filter, sort, segment, hasLoadedSegmentById } = this.state
    const { segmentId } = match.params

    if (!!segmentId && !hasLoadedSegmentById) return this.fetchAccountsForSegment()
    if (!!segment && isRuleSetValid(segment)) return this.fetchAccountsWithRuleSet()

    try {
      const { data:accountsResponse } = await getAccounts({ sort, size, filter, index: index * size })
      onReceiveAccounts(accountsResponse)
      this.setAccountsLoaded()
    } catch (err) {
      showTransientNotification({ type: 'error', message: 'We had a problem loading your accounts.'})
      this.setAccountsLoaded()
    }
  }

  updateAccountFollow = async toggle => {
    const { showTransientNotification, onReceiveUserSettings } = this.props

    try {
      const { data } = await setAccountsFollow(toggle)
      onReceiveUserSettings(data)

      showTransientNotification({ type: 'success', message: 'Your settings have been updated.'})
    } catch (err) {
      showTransientNotification({ type: 'error', message: 'We had a problem turning on your notifications.'})
    }
  }

  updateFilterData = segment => {
    if (!this.state.filterMode) return

    this.setState({
      segment: segment,
      isFetchingNew: true,
      index: 0
    }, this.fetchAccounts)
  }

  changeSegmentToFetch = async segment => {
    if (!this.state.filterMode) return
    this.setState({
      segment: segment,
      resultSwap: true,
      isFetchingNew: true,
      accountsHasLoaded: false,
      hasLoadedSegmentById: false,
      filterMode: false,
      index: 0
    })

    await delay(this.fetchAccounts, 10)
  }

  clearSelected = () => {
    this.setState({
      selectedRows: []
    })
  }

  updateSort = (value, direction) => {
    const getOppositeDirection = direction => {
      return direction === 'asc' ? 'desc' : 'asc'
    }

    this.setState({
      sort: {
        dir: getOppositeDirection(direction),
        property: value
      },
      isFetchingNew: true
    }, this.fetchAccounts)
  }

  toggleAccountFollow = async event => {
    const checked = event.target.checked
    this.updateAccountFollow(checked)
  }

  openModal = () => {
    this.setState({ modalIsOpen: true })
  }

  closeModal = () => {
    this.setState({ modalIsOpen: false })
  }

  render() {
    const {
      confirmDeleteModalOpen,
      accountsLoading,
      accountsHasLoaded,
      isFetchingNew ,
      index,
      size,
      showOnboarding,
      hasABMPlaybooks,
      selectedRows,
      selectedAccount,
      deleteLoading,
      segment,
      showCreateOrEditAccountSidebar,
      resultSwap,
      canDeleteAll,
      isDeletingAll,
      modalIsOpen
    } = this.state

    const { users, accounts, settings, showTransientNotification, bootstrapComplete, location } = this.props
    this.createSegment = queryString.parse(location.search).createSegment === "true" ? true : false

    const hasFollowAccounts = !!settings.follow_accounts
    const hasAccounts = !!accounts.results && !!accounts.results.length
    const shouldShowOnboarding = showOnboarding && (!hasFollowAccounts || !hasAccounts || !hasABMPlaybooks) && !accountsLoading
    const totalPages = Math.ceil(accounts.totalResults/size)

    const selectedIds = (accounts.results && accounts.results.length) ?
      pickByIndex(selectedRows, accounts.results).filter((account) => Boolean(account.accountId))
        .map(({accountId}) => accountId) :
      []

    const tableColumns = this.getFormattedColumns()

    return (
      <Fragment>
      { modalIsOpen && <AccountSettingsModal onClose={this.closeModal} showTransientNotification={showTransientNotification} /> }
      <Div className="accounts-nav-header">
        <NavigationHeader title={segment ? segment.name : 'All accounts'}>
          {accountsHasLoaded &&
            <>
              {!access.hasGates('saved_filters_push_notifications') && 
                <IconButton className='account-settings-cog' icon='settings-1' onClick={this.openModal} />}
              <ManageAccounts
                addNew={this.toggleCreateOrEditNewAccountSidebar}
              />
            </>
          }
        </NavigationHeader>
        </Div>

        <Div className= {classnames("accounts-home", {
          'accounts-home--loaded': accountsHasLoaded || resultSwap,
          'accounts-home--fetching': isFetchingNew
        })}>

          <AccountsOnboarding
            hasFollowAccounts={hasFollowAccounts}
            toggleAccountFollow={this.toggleAccountFollow}
            onHide={this.dismissOnboarding}
            onHideForever={this.dismissOnboardingForever}
            openCreateOrEditAccountSidebar={this.toggleCreateOrEditNewAccountSidebar}
            show={shouldShowOnboarding}
          />

          <AccountTableToolbar
            toggleConfirmDeleteModal={this.toggleConfirmDeleteModal}
            accounts={accounts}
            searchChange={this.setSearchString}
            searchText={this.state.searchString}
            selectedRows={selectedRows}
            deleteLoading={deleteLoading}
            segment={segment}
            onFilterUpdate={this.updateFilterData}
            canDeleteAll={canDeleteAll}
            createSegment={this.createSegment}
          />
          {(accountsLoading || resultSwap || !bootstrapComplete) && <Loading hasFinished={!resultSwap && accountsHasLoaded && bootstrapComplete} />}
          {accounts.results && !resultSwap && bootstrapComplete &&
            <Fragment>
              <AccountsTable
                accounts={accounts.results}
                columns={tableColumns}
                users={users}
                selectedRows={selectedRows}
                selectRow={this.selectRow}
                removeRow={this.removeRow}
                onChange={this.selectRow}
                hasFinished={accountsHasLoaded}
                openEdit={this.toggleCreateOrEditNewAccountSidebar}
                openDelete={this.setSingleDelete}
                isWorking={isFetchingNew}
              />

              {!!!segment &&
                <AccountsTableZeroState
                  accounts={accounts}
                  openCreateOrEditAccountSidebar={this.toggleCreateOrEditNewAccountSidebar}
                  loading={accountsLoading}
                />
              }

              {!accountsLoading &&
                <Paginator
                  goBackward={this.navigateBackward}
                  goForward={this.navigateForward}
                  index={index}
                  totalPages={totalPages}
                  size={size}
                  changeSize={this.onChangeSize}
                />
              }
            </Fragment>
          }

          {showCreateOrEditAccountSidebar &&
            <CreateOrEditAccountSidebar
              accountOwners={users}
              accountProfile={selectedAccount}
              close={this.toggleCreateOrEditNewAccountSidebar}
              fetchAccounts={this.fetchAccounts}
              open={showCreateOrEditAccountSidebar}
            />
          }

          <DeleteAccounts
            showTransientNotification={showTransientNotification}
            accountIds={selectedIds}
            fetchAccounts={this.fetchAccounts}
            setAccountsLoaded={this.setAccountsLoaded}
            open={confirmDeleteModalOpen}
            toggle={this.toggleConfirmDeleteModal}
            setLoading={this.setDeleteLoading}
            totalAccounts={accounts.totalResults}
            isDeletingAll={isDeletingAll}
            clearSelected={this.clearSelected}
          />
        </Div>
      </Fragment>
    )
  }
}

export default AccountsHome
